import { ArrowRight, Award, ExternalLink, Truck } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { PendingArrival } from "@/features/intake/api/types"
import { useConfirmArrival } from "@/features/intake/hooks/use-intake-mutations"
import { certificateTranslationKey } from "@/lib/certificates"
import { formatNumber, kgToMt } from "@/lib/format"
import { paths } from "@/routes/paths"

interface WaveInDialogProps {
  pending: PendingArrival | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface MetaProps {
  label: string
  value: string
  mono?: boolean
}

function Meta({ label, value, mono = true }: MetaProps) {
  return (
    <div className="space-y-0.5">
      <div className="text-muted-foreground text-[10px] uppercase tracking-wide">
        {label}
      </div>
      <div className={mono ? "font-mono text-sm tabular-nums" : "text-sm"}>
        {value}
      </div>
    </div>
  )
}

export function WaveInDialog({ pending, open, onOpenChange }: WaveInDialogProps) {
  const { t } = useTranslation(["intake", "common"])
  const navigate = useNavigate()
  const confirm = useConfirmArrival()

  if (!pending) return null

  const cooperative = pending.orderId?.groupId?.name ?? "—"
  const slip = pending.billOfLadingNumber ?? `#${pending.id}`
  const order = pending.orderId?.orderNumber ?? "—"
  const truck = pending.truckRegistrationNumber ?? null
  const kg = pending.netKilogramsAttached ?? 0
  const certificates = pending.orderId?.certificates ?? []

  const handleConfirm = () => {
    confirm.mutate(
      { deliveryFormId: pending.id },
      {
        onSuccess: (result) => {
          onOpenChange(false)
          navigate(paths.receptionDetail(result.id))
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("intake:waveIn.title")}</DialogTitle>
          <DialogDescription>
            {t("intake:waveIn.subtitle", { slip })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-muted/40 rounded-md border p-4">
            <div className="flex items-center gap-3">
              <div className="bg-background text-muted-foreground flex size-9 items-center justify-center rounded-md border">
                <Truck className="size-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{cooperative}</div>
                <div className="text-muted-foreground truncate text-xs">
                  {truck ?? t("intake:waveIn.noTruck")}
                </div>
              </div>
              <div className="text-right">
                <div className="text-muted-foreground text-[10px] uppercase tracking-wide">
                  {t("intake:waveIn.declaredWeight")}
                </div>
                <div className="flex items-baseline justify-end gap-1.5">
                  <span className="text-lg font-semibold tabular-nums">
                    {formatNumber(kgToMt(kg), 1)}
                  </span>
                  <span className="text-muted-foreground text-xs font-medium">
                    MT
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Meta label={t("intake:waveIn.slip")} value={slip} />
            <Meta label={t("intake:waveIn.order")} value={order} />
            <Meta
              label={t("intake:waveIn.truck")}
              value={truck ?? t("intake:waveIn.noTruck")}
            />
          </div>

          {certificates.length > 0 ? (
            <div className="space-y-1.5">
              <div className="text-muted-foreground inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide">
                <Award className="size-3" />
                {t("intake:waveIn.certificates")}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {certificates.map((cert) => {
                  const key = certificateTranslationKey(cert)
                  const label = key ? t(key) : cert
                  return (
                    <Badge
                      key={cert}
                      variant="secondary"
                      className="text-[11px] font-normal"
                    >
                      {label}
                    </Badge>
                  )
                })}
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            variant="link"
            size="sm"
            asChild
            className="px-0 text-muted-foreground hover:text-foreground"
          >
            <a
              href={paths.deliveryFormDetail(pending.id)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="size-3.5" />
              {t("intake:waveIn.viewTrace")}
            </a>
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={confirm.isPending}
            >
              {t("intake:waveIn.cancel")}
            </Button>
            <Button onClick={handleConfirm} disabled={confirm.isPending}>
              {confirm.isPending ? null : <ArrowRight className="size-4" />}
              {t("intake:waveIn.confirm")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
